import React, { memo } from 'react';
import { webSiteUrlRegex } from 'utils/helpers';
import { ValidationErrors } from 'constants/validations';
import { TagSelect, UploadContent } from 'components/common';
import { Checkbox, Col, Form, FormModal, Input, Row, Select, Typography, Upload } from 'components';
import { useBrandDetailsEdit } from './useBrandDetailsEdit';

const { Dragger } = Upload;
const { Item } = Form;
const { TextArea } = Input;

export interface IBrandDetailsUpdate {
  onCancel: () => void;
  open?: boolean;
}

export const BrandDetailsEdit = memo<IBrandDetailsUpdate>((props) => {
  const [form] = Form.useForm();
  const { onCancel, open } = props;

  const {
    onFinish,
    contextHolder,
    tagData,
    industryData,
    countryData,
    isSaveBtnDisabled,
    checkErrors,
    onCancelModal,
    isLoading,
    handleFileSizeValidation,
  } = useBrandDetailsEdit({
    onCancel,
    form,
    open,
  });

  return (
    <>
      {contextHolder}
      <FormModal
        form={form}
        title={'Update Brand Details'}
        okText={'Save'}
        open={open}
        isLoading={isLoading}
        disableOkBtn={isSaveBtnDisabled}
        onValuesChange={checkErrors}
        onCancel={onCancelModal}
        onSubmit={onFinish}
      >
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Item
              name="name"
              rules={[
                {
                  required: true,
                  message: ValidationErrors.required('Brand Name'),
                },
              ]}
              label={'Brand Name*'}
            >
              <Input size={'large'} />
            </Item>
          </Col>
          <Col span={12}>
            <Item
              label={'Brand Logo'}
              name="logo"
              valuePropName="fileList"
              getValueFromEvent={(e) => e?.fileList || []}
            >
              <Dragger
                beforeUpload={handleFileSizeValidation}
                onRemove={() => {
                  checkErrors();
                }}
                accept={'.jpg, .jpeg, .png'}
                maxCount={1}
              >
                <UploadContent
                  title={'Upload or Drag and Drop IMG'}
                  message={'Max size: 5MB / Format: JPG, JPEG, PNG'}
                />
              </Dragger>
            </Item>
          </Col>
          <Col span={12}>
            <Item
              name="webSiteUrl"
              label={'Brand Website'}
              rules={[{ pattern: webSiteUrlRegex, message: 'Please enter a valid website URL.' }]}
            >
              <Input size={'large'} />
            </Item>
          </Col>
          <Col span={12}>
            <Item name="countryId" label={'Main Country'}>
              <Select
                showSearch
                options={countryData}
                size={'large'}
                filterOption={(input, option) => {
                  if (option) {
                    const label: string = option.label?.toString() || '';
                    return label?.toLowerCase()?.includes(input?.toLowerCase());
                  }
                  return false;
                }}
              />
            </Item>
          </Col>
          <Col span={12}>
            <Item
              name="industryId"
              rules={[{ required: true, message: ValidationErrors.required('Industry') }]}
              label={'Industry'}
            >
              <Select options={industryData} size={'large'} />
            </Item>
          </Col>
          <Col span={12}>
            <Item
              name="manufacturerWebSiteUrl"
              label={'Manufacturer Website'}
              rules={[{ pattern: webSiteUrlRegex, message: 'Please enter a valid website URL.' }]}
            >
              <Input size={'large'} />
            </Item>
          </Col>
          <Col span={24}>
            <Item name="officialDealer" valuePropName={'checked'}>
              <Checkbox>
                <Typography.Text style={{ fontWeight: 500, color: '#05796B' }}>Official Dealer</Typography.Text>
              </Checkbox>
            </Item>
          </Col>
          <Col span={24}>
            <Item label={'Additional Notes'} name={'additionalNotes'}>
              <TextArea rows={4} />
            </Item>
          </Col>
          <Col span={24}>
            <TagSelect data={tagData} name={'manualTags'} label={'Tags'} />
          </Col>
        </Row>
      </FormModal>
    </>
  );
});
